import { useState } from "react";
import { CheckCircle2, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const inputCls = "bg-white border border-border focus:border-gold px-3 py-2.5 w-full outline-none transition text-sm rounded-none";

export default function Settings() {
  const { user } = useAuth();
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [changed, setChanged] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!pw.current_password) errs.current = "Current password is required";
    if (pw.new_password.length < 8) errs.next = "At least 8 characters";
    if (pw.new_password !== pw.confirm) errs.confirm = "Passwords do not match";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated");
      setChanged(true);
      setPw({ current_password: "", new_password: "", confirm: "" });
      setTimeout(() => setChanged(false), 4000);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" description="Account security and workspace preferences." testId="settings-header" />

      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
        <section className="bg-white border border-border p-6 sm:p-8" data-testid="settings-account">
          <h2 className="font-serif text-2xl font-semibold text-navy mb-6 flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-gold-700" /> Account
          </h2>
          <dl className="space-y-4 text-sm">
            <div className="flex justify-between border-b border-border pb-3">
              <dt className="text-muted-foreground">Name</dt>
              <dd className="font-semibold text-navy">{user?.name}</dd>
            </div>
            <div className="flex justify-between border-b border-border pb-3">
              <dt className="text-muted-foreground">Email</dt>
              <dd className="font-semibold text-navy">{user?.email}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Role</dt>
              <dd className="font-semibold text-navy capitalize">{user?.role}</dd>
            </div>
          </dl>
        </section>

        <section className="bg-white border border-border p-6 sm:p-8" data-testid="settings-password">
          <h2 className="font-serif text-2xl font-semibold text-navy mb-6 flex items-center gap-3">
            <KeyRound className="h-5 w-5 text-gold-700" /> Change Password
          </h2>
          {changed && (
            <div className="mb-5 bg-emerald-50 border-l-4 border-emerald-500 px-4 py-3 text-sm text-emerald-700 flex items-center gap-2" data-testid="password-changed">
              <CheckCircle2 className="h-4 w-4" /> Password updated successfully.
            </div>
          )}
          <form onSubmit={submit} className="space-y-5" noValidate>
            <Field label="Current Password" error={errors.current} htmlFor="pw-current">
              <input id="pw-current" type="password" className={inputCls} value={pw.current_password}
                onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="current-password-input" autoComplete="current-password" />
            </Field>
            <Field label="New Password" error={errors.next} htmlFor="pw-new">
              <input id="pw-new" type="password" className={inputCls} value={pw.new_password}
                onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="new-password-input" autoComplete="new-password" />
            </Field>
            <Field label="Confirm New Password" error={errors.confirm} htmlFor="pw-confirm">
              <input id="pw-confirm" type="password" className={inputCls} value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="confirm-password-input" autoComplete="new-password" />
            </Field>
            <Button type="submit" disabled={saving} data-testid="change-password-btn"
              className="bg-navy text-ivory hover:bg-navy-800 rounded-none uppercase tracking-wider text-xs font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
            </Button>
          </form>
        </section>

        <section className="bg-white border border-border p-6 sm:p-8 lg:col-span-2" data-testid="settings-preferences">
          <h2 className="font-serif text-2xl font-semibold text-navy mb-2">Workspace Preferences</h2>
          <p className="text-sm text-muted-foreground mb-6">These preferences are saved to your account.</p>
          <div className="space-y-5 max-w-lg">
            {[
              ["email_notifications", "Inquiry alerts", "Notify when a contact or consultation request arrives."],
              ["appointment_reminders", "Appointment reminders", "Surface tomorrow's appointments in notifications."],
            ].map(([key, label, desc]) => (
              <div key={key} className="flex items-center justify-between gap-6 border border-border px-5 py-4">
                <div>
                  <p className="text-sm font-semibold text-navy">{label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                </div>
                <Switch defaultChecked={user?.prefs?.[key] !== false} data-testid={`pref-${key}`} />
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
